import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { DataGrid } from "@material-ui/data-grid";
import ReportDownloadButton from "./ReportDownloadButton";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { useDocumentDataOnce } from "react-firebase-hooks/firestore";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { Column } from "../../widgets";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";

const useStyles = makeStyles((theme) => ({
  buttonRow: {
    flex: "0 1 auto",
    display: "flex",
    flexDirection: "row",
  },
  grid: {
    flex: "1 1 auto",
    margin: theme.spacing(2),
  },
  create: {
    flex: "0 1 auto",
    marginBottom: theme.spacing(1),
  },
  spacer: {
    flex: "1 1 auto",
  },
  orgSelect: {
    flex: "0 1 auto",
    marginBottom: theme.spacing(1),
    minWidth: 200,
  },
  // Descriptions are free text and often run to a paragraph, so the cell shows
  // the opening of one and defers the rest to the row's detail view.
  description: {
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
  },
}));

const dateFormat = new Intl.DateTimeFormat("default", {
  dateStyle: "short",
  timeStyle: "short",
});

const makeColumns = (renderers) => [
  {
    field: "date",
    headerName: "Date",
    width: 200,
  },
  {
    field: "inspector",
    headerName: "Inspector",
    width: 200,
  },
  {
    field: "location",
    headerName: "Location",
    width: 150,
  },
  {
    field: "city",
    headerName: "City",
    width: 150,
  },
  {
    field: "state",
    headerName: "State",
    width: 125,
  },
  // Given the leftover width rather than a fixed one, so the columns that
  // identify a row keep their size and the grid does not scroll sideways.
  {
    field: "description",
    headerName: "Description",
    renderCell: renderers.descriptionCell,
    flex: 1,
    minWidth: 200,
  },
  {
    field: "samples",
    headerName: "Samples",
    width: 150,
  },
  {
    field: "report",
    headerName: "Report",
    renderCell: renderers.downloadButtons,
    disableColumnMenu: true,
    sortable: false,
    width: 150,
  },
  ...(renderers.isSuperAdmin ? [
    {
      field: "delete",
      headerName: " ",
      renderCell: renderers.deleteButton,
      disableColumnMenu: true,
      sortable: false,
      width: 60,
    },
  ] : []),
];

function mapSnapshotToRows(snapshot) {
  if (!snapshot) return [];

  const rows = snapshot.docs
    .filter((doc) => {
      const data = doc.data();
      return !data.isHidden;
    })
    .map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        date: dateFormat.format(data.createdAt?.toDate()),
        inspector: data.inspector,
        location: data.location,
        city: data.city,
        state: data.state,
        // Normalized so inspections predating the field sort alongside ones
        // the inspector deliberately left blank.
        description: data.jobDescription ?? "",
        samples: data.sampleCount ?? 0,
        report: doc.id,
      };
    });

  return rows;
}

function InspectionListExpanded(props) {
  const {
    organizationId,
    loading,
    snapshot,
    error,
    onItemSelected,
    onShowSnackbar,
    onDeleteClick,
    isSuperAdmin,
    combineMode,
    selectionModel,
    onSelectionChange,
  } = props;
  const classes = useStyles();

  const [organization, orgLoading, orgErrorIgnored] = useDocumentDataOnce(
    firebase.firestore().doc(`organizations/${organizationId}`)
  );

  useEffect(() => {
    document.title = "Inspections - Runyon Data Services";
  });

  // Selecting a row normally opens it. While combining, selection means
  // "include in the master report" instead, so navigation is suppressed rather
  // than the two behaviours fighting over the same event.
  const handleSelectionModelChange = (nextSelectionModel) => {
    if (combineMode) {
      onSelectionChange(nextSelectionModel);
      return;
    }

    onItemSelected(
      nextSelectionModel.length > 0 ? nextSelectionModel[0] : undefined
    );
  };

  const handleReportReady = (downloadUrl) => {
    window.location = downloadUrl;
  };

  const handleError = (error) => {
    onShowSnackbar({
      open: true,
      message: "An unexpected error occurred while building your report.",
      severity: "error",
      error: error,
    });
  };

  const handleCellClick = (params, event) => {
    if (params.colDef.field == "report" || params.colDef.field == "delete") {
      event.stopPropagation();
    }
  };

  const reportTypes = organization?.reportSettings?.supportedFormats;

  const downloadButtons = (params) => {
    return (
      <React.Fragment>
        {orgLoading && <CircularProgress />}
        {reportTypes && (
          <ReportDownloadButton
            inspectionId={params.value}
            onReportReady={handleReportReady}
            onError={handleError}
            reportTypes={reportTypes}
          />
        )}
      </React.Fragment>
    );
  };

  const deleteButton = (params) => {
    return (
      <IconButton
        aria-label="delete"
        size="small"
        onClick={() => onDeleteClick(params.row.id)}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    );
  };

  // The full text is exposed through the native tooltip because the cell only
  // ever shows one truncated line.
  const descriptionCell = (params) => {
    if (!params.value) return null;

    return (
      <span className={classes.description} title={params.value}>
        {params.value}
      </span>
    );
  };

  const columns = makeColumns({
    descriptionCell: descriptionCell,
    downloadButtons: downloadButtons,
    deleteButton: deleteButton,
    isSuperAdmin: isSuperAdmin,
  });
  const rows = mapSnapshotToRows(snapshot);

  return (
    <React.Fragment>
      {rows.length == 0 && !loading && (
        <Column spacing={2} horizontalAlignment="centerHorizontally">
          <img src="images/NoDocuments@2x.png" />
          <Typography>There are no inspections to display</Typography>
        </Column>
      )}
      {(loading || rows.length > 0) && (
        <DataGrid
          className={classes.grid}
          rows={rows}
          columns={columns}
          loading={loading}
          error={error}
          onSelectionModelChange={handleSelectionModelChange}
          onCellClick={handleCellClick}
          checkboxSelection={combineMode}
          {...(combineMode ? { selectionModel: selectionModel } : {})}
        />
      )}
    </React.Fragment>
  );
}

InspectionListExpanded.propTypes = {
  organizationId: PropTypes.string,
  loading: PropTypes.bool,
  snapshot: PropTypes.object,
  error: PropTypes.object,
  onItemSelected: PropTypes.func,
  onShowSnackbar: PropTypes.func,
  onDeleteClick: PropTypes.func,
  isSuperAdmin: PropTypes.bool,
  combineMode: PropTypes.bool,
  selectionModel: PropTypes.arrayOf(PropTypes.string),
  onSelectionChange: PropTypes.func,
};

export default InspectionListExpanded;
